import { database } from '../config/backend';
import { ref, update as rtdbUpdate, get as rtdbGet } from './phpDataAdapter';
import { LiveStream } from '../types/video';
import muxLiveService, { MuxLiveStream } from './muxLiveService';
import { createMuxLiveStream, endMuxLiveStream } from '../config/mux';

/**
 * Converte MuxLiveStream para LiveStream
 */
function convertMuxToLiveStream(muxStream: MuxLiveStream): LiveStream {
  return {
    id: muxStream.id,
    channelName: muxStream.id, // Usar ID como channelName se não houver
    hostId: muxStream.hostId,
    title: muxStream.title,
    description: muxStream.description,
    status: muxStream.status,
    startedAt: muxStream.startedAt,
    endedAt: muxStream.endedAt,
    duration: muxStream.endedAt && muxStream.startedAt
      ? Math.floor((muxStream.endedAt.getTime() - muxStream.startedAt.getTime()) / 1000)
      : undefined,
    viewerCount: muxStream.viewerCount,
    createdAt: muxStream.createdAt,
    // Propriedades do Mux
    muxStreamId: muxStream.muxStreamId,
    muxStreamKey: muxStream.muxStreamKey,
    muxPlaybackId: muxStream.muxPlaybackId,
    rtmpUrl: muxStream.rtmpUrl,
    playbackUrl: muxStream.playbackUrl,
  };
}

/**
 * Cria uma nova live stream
 */
export async function createLiveStream(
  hostId: string,
  title: string,
  description?: string,
  allowedPatientIds?: string[]
): Promise<LiveStream> {
  const muxStream = await muxLiveService.createLiveStream(hostId, title, description);
  return convertMuxToLiveStream(muxStream);
}

/**
 * Inicia uma live stream (host)
 */
export async function startLiveStream(streamId: string, hostId: string): Promise<void> {
  try {
    const stream = await muxLiveService.getLiveStream(streamId);
    if (!stream) {
      throw new Error('Live stream não encontrada');
    }

    if (stream.hostId !== hostId) {
      throw new Error('Usuário não autorizado');
    }

    await muxLiveService.startBroadcast(streamId);
  } catch (error) {
    console.error('Erro ao iniciar live stream:', error);
    throw error;
  }
}

/**
 * Finaliza uma live stream
 */
export async function endLiveStream(streamId: string): Promise<void> {
  try {
    await muxLiveService.endBroadcast(streamId);
  } catch (error) {
    console.error('Erro ao finalizar live stream:', error);
    throw error;
  }
}

/**
 * Entra em uma live stream como viewer
 */
export async function joinLiveStream(streamId: string, userId: string): Promise<void> {
  try {
    await muxLiveService.incrementViewerCount(streamId);
  } catch (error) {
    console.error('Erro ao entrar na live stream:', error);
    throw error;
  }
}

/**
 * Sai de uma live stream como viewer
 */
export async function leaveLiveStream(streamId: string, userId: string): Promise<void> {
  try {
    await muxLiveService.decrementViewerCount(streamId);
  } catch (error) {
    console.error('Erro ao sair da live stream:', error);
    throw error;
  }
}

/**
 * Busca uma live stream
 */
export async function getLiveStream(streamId: string): Promise<LiveStream | null> {
  const muxStream = await muxLiveService.getLiveStream(streamId);
  return muxStream ? convertMuxToLiveStream(muxStream) : null;
}

/**
 * Busca lives ativas
 */
export async function getActiveLiveStreams(hostId?: string): Promise<LiveStream[]> {
  const muxStreams = await muxLiveService.getActiveLiveStreams(hostId);
  return muxStreams.map(convertMuxToLiveStream);
}

/**
 * Observa mudanças em uma live stream
 */
export function watchLiveStream(
  streamId: string,
  callback: (stream: LiveStream | null) => void
): () => void {
  return muxLiveService.watchLiveStream(streamId, (muxStream) => {
    callback(muxStream ? convertMuxToLiveStream(muxStream) : null);
  });
}
